import React, { useEffect, useState } from "react";
import { api, RSD, imgURL } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { BagLogo } from "../../components/Logo";
import { ImageUpload } from "../../components/ImageUpload";
import { Store, UtensilsCrossed, Users, ShoppingBag, Plus, Trash2, Pencil, X, Power, SlidersHorizontal, Languages, BarChart3, Clock } from "lucide-react";
import { toast } from "sonner";
import { OptionsManager } from "./OptionsManager";
import PlatformAnalytics from "./PlatformAnalytics";
import { TeamManager } from "../restaurant/TeamPage";
import { SortableList, SortableRow, arrayMove } from "../../components/SortableList";
import { InlineRename } from "../../components/InlineRename";
import { CategoryScheduleEditor, ScheduleBadge } from "../../components/CategoryScheduleEditor";

const DAYS = [["monday", "Ponedeljak"], ["tuesday", "Utorak"], ["wednesday", "Sreda"], ["thursday", "Četvrtak"], ["friday", "Petak"], ["saturday", "Subota"], ["sunday", "Nedelja"]];
const NAV = [
  { key: "restaurants", label: "Restorani", icon: Store },
  { key: "menu", label: "Meni", icon: UtensilsCrossed },
  { key: "options", label: "Opcije", icon: SlidersHorizontal },
  { key: "translations", label: "Prevodi", icon: Languages },
  { key: "orders", label: "Porudžbine", icon: ShoppingBag },
  { key: "users", label: "Korisnici", icon: Users },
  { key: "analytics", label: "Analitika", icon: BarChart3 },
];
const inputCls = "w-full px-3 py-2 rounded-lg border-2 border-slate-200 outline-none focus:ring-2 focus:ring-brand-red";
function Field({ label, children }) { return <label className="block mb-3"><span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span><div className="mt-1">{children}</div></label>; }
const defaultHours = () => Object.fromEntries(DAYS.map(([k]) => [k, { open: "10:00", close: "23:00", closed: false }]));

/* -------- Restaurant editor -------- */
function RestaurantEditor({ restaurant, onClose, onSaved }) {
  const [f, setF] = useState(() => restaurant || { name: "", category: "", description: "", address: "", phone: "", cover_image: "", logo: "", opening_hours: defaultHours(), prep_time: 15, pickup_interval: 15, max_orders_per_slot: 5, normal_pickup_range: "15_20", payment_method: "BOTH", is_open: true, temp_closed: false, accepting_orders: true, active: true });
  const [cities, setCities] = useState([]);
  React.useEffect(() => { api.get("/cities").then(({ data }) => setCities(data || [])).catch(() => {}); }, []);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setHour = (d, k, v) => setF((p) => ({ ...p, opening_hours: { ...p.opening_hours, [d]: { ...p.opening_hours[d], [k]: v } } }));
  const save = async () => {
    if (!f.name) { toast.error("Naziv je obavezan"); return; }
    try {
      if (restaurant?.id) await api.put(`/admin/restaurants/${restaurant.id}`, f);
      else await api.post(`/admin/restaurants`, f);
      toast.success("Sačuvano"); onSaved();
    } catch { toast.error("Greška pri čuvanju"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4"><h3 className="font-display font-black text-xl">{restaurant?.id ? "Izmeni restoran" : "Novi restoran"}</h3><button onClick={onClose} className="bg-slate-100 rounded-full p-2"><X size={16} /></button></div>
        <Field label="Naziv"><input data-testid="rest-name" className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} /></Field>
        <Field label="Kuhinja / kategorija"><input className={inputCls} value={f.category} onChange={(e) => set("category", e.target.value)} placeholder="npr. Šaorma • Brza hrana" /></Field>
        <Field label="Opis"><textarea className={inputCls} value={f.description} onChange={(e) => set("description", e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Adresa"><input className={inputCls} value={f.address} onChange={(e) => set("address", e.target.value)} /></Field>
          <Field label="Telefon"><input className={inputCls} value={f.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
        </div>
        <Field label="Grad (određuje administrator)">
          <select data-testid="admin-city-select" className={inputCls} value={f.city_id || ""} onChange={(e) => set("city_id", e.target.value)}>
            <option value="">— Izaberi grad —</option>
            {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <ImageUpload label="Cover slika" value={f.cover_image} onChange={(v) => set("cover_image", v)} />
        <ImageUpload label="Logo" value={f.logo} onChange={(v) => set("logo", v)} />
        <div className="grid grid-cols-3 gap-3">
          <Field label="Priprema (min)"><input type="number" className={inputCls} value={f.prep_time} onChange={(e) => set("prep_time", Number(e.target.value))} /></Field>
          <Field label="Interval (min)"><input type="number" className={inputCls} value={f.pickup_interval} onChange={(e) => set("pickup_interval", Number(e.target.value))} /></Field>
          <Field label="Max po terminu"><input type="number" className={inputCls} value={f.max_orders_per_slot} onChange={(e) => set("max_orders_per_slot", Number(e.target.value))} /></Field>
        </div>
        <Field label="Prikaz vremena preuzimanja (kupcu)">
          <select data-testid="pickup-range-select" className={inputCls} value={f.normal_pickup_range || "15_20"} onChange={(e) => set("normal_pickup_range", e.target.value)}>
            <option value="10_15">10–15 min</option>
            <option value="15_20">15–20 min</option>
            <option value="20_30">20–30 min</option>
            <option value="30_PLUS">30+ min</option>
          </select>
        </Field>
        <Field label="Način plaćanja">
          <select data-testid="payment-method-select" className={inputCls} value={f.payment_method || "BOTH"} onChange={(e) => set("payment_method", e.target.value)}>
            <option value="CASH">Gotovina</option>
            <option value="CARD">Kartica</option>
            <option value="BOTH">Gotovina i kartica</option>
          </select>
        </Field>
        <div className="flex flex-wrap gap-4 mt-1 mb-3">
          <label className="flex items-center gap-2 font-semibold"><input data-testid="toggle-open" type="checkbox" checked={f.is_open} onChange={(e) => set("is_open", e.target.checked)} /> Otvoreno</label>
          <label className="flex items-center gap-2 font-semibold"><input type="checkbox" checked={f.temp_closed} onChange={(e) => set("temp_closed", e.target.checked)} /> Privremeno zatvoreno</label>
          <label className="flex items-center gap-2 font-semibold text-brand-orange"><input data-testid="toggle-accepting" type="checkbox" checked={f.accepting_orders !== false} onChange={(e) => set("accepting_orders", e.target.checked)} /> Prima porudžbine</label>
          <label className="flex items-center gap-2 font-semibold"><input type="checkbox" checked={f.active !== false} onChange={(e) => set("active", e.target.checked)} /> Aktivan (vidljiv kupcima)</label>
        </div>
        <div className="border-t pt-3">
          <h4 className="font-bold text-sm mb-2">Radno vreme</h4>
          {DAYS.map(([k, label]) => (
            <div key={k} className="flex items-center gap-3 mb-2">
              <span className="w-24 text-sm font-semibold">{label}</span>
              <input type="time" className="px-2 py-1.5 rounded-lg border-2 border-slate-200" value={f.opening_hours[k].open} onChange={(e) => setHour(k, "open", e.target.value)} disabled={f.opening_hours[k].closed} />
              <span>–</span>
              <input type="time" className="px-2 py-1.5 rounded-lg border-2 border-slate-200" value={f.opening_hours[k].close} onChange={(e) => setHour(k, "close", e.target.value)} disabled={f.opening_hours[k].closed} />
              <label className="text-xs flex items-center gap-1 ml-1"><input type="checkbox" checked={f.opening_hours[k].closed} onChange={(e) => setHour(k, "closed", e.target.checked)} />Zatvoreno</label>
            </div>
          ))}
        </div>
        <button data-testid="save-restaurant" onClick={save} className="w-full bg-brand-red text-white rounded-lg py-3 font-bold mt-4">Sačuvaj</button>
      </div>
    </div>
  );
}

function ManageAccess({ restaurant, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="manage-access">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4"><h3 className="font-display font-black text-lg">Tim · {restaurant.name}</h3><button onClick={onClose} className="bg-slate-100 rounded-full p-2"><X size={16} /></button></div>
        <TeamManager rid={restaurant.id} actorRole="PLATFORM_ADMIN" />
      </div>
    </div>
  );
}

function RestaurantsManager({ restaurants, reload, isAdmin }) {
  const [editing, setEditing] = useState(null);
  const [access, setAccess] = useState(null);
  const toggleActive = async (r) => { await api.patch(`/admin/restaurants/${r.id}/active`, { active: !r.active }); reload(); };
  return (
    <div>
      {isAdmin && <button data-testid="new-restaurant" onClick={() => setEditing({})} className="mb-4 bg-brand-red text-white rounded-lg px-4 py-2 font-semibold flex items-center gap-1"><Plus size={16} />Novi restoran</button>}
      <div className="grid sm:grid-cols-2 gap-4 max-w-3xl">
        {restaurants.map((r) => (
          <div key={r.id} data-testid={`admin-rest-${r.slug}`} className="bg-white rounded-xl border overflow-hidden">
            <div className="h-24 bg-slate-100">{r.cover_image && <img src={imgURL(r.cover_image)} className="w-full h-full object-cover" alt="" />}</div>
            <div className="p-3">
              <div className="flex items-center justify-between"><p className="font-display font-black">{r.name}</p><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.active ? "bg-brand-green/10 text-brand-green" : "bg-slate-200 text-slate-500"}`}>{r.active ? "Aktivan" : "Neaktivan"}</span></div>
              <p className="text-xs text-slate-400">{r.category} · /{r.slug}</p>
              <div className="flex flex-wrap gap-3 mt-2">
                <button onClick={() => setEditing(r)} className="text-sm font-semibold text-slate-700 flex items-center gap-1"><Pencil size={14} />Izmeni</button>
                {isAdmin && <button data-testid={`access-${r.slug}`} onClick={() => setAccess(r)} className="text-sm font-semibold text-brand-red flex items-center gap-1"><Users size={14} />Pristup</button>}
                {isAdmin && <button onClick={() => toggleActive(r)} className="text-sm font-semibold text-brand-orange flex items-center gap-1"><Power size={14} />{r.active ? "Deaktiviraj" : "Aktiviraj"}</button>}
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing !== null && <RestaurantEditor restaurant={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
      {access && <ManageAccess restaurant={access} onClose={() => setAccess(null)} />}
    </div>
  );
}

/* -------- Product discount (POPUST) -------- */
const PCT_PRESETS = [5, 10, 15, 20, 25, 30];
const DISC_LABEL = { ACTIVE: "AKTIVNO", SCHEDULED: "ZAKAZANO", EXPIRED: "ISTEKLO", INACTIVE: "NEAKTIVAN", NONE: "BEZ POPUSTA" };
const DISC_CLS = {
  ACTIVE: "bg-brand-green/10 text-brand-green", SCHEDULED: "bg-amber-100 text-amber-700",
  EXPIRED: "bg-slate-200 text-slate-500", INACTIVE: "bg-slate-200 text-slate-500", NONE: "bg-slate-100 text-slate-400",
};
export function discountLabel(d) {
  if (!d) return "";
  return d.type === "PERCENTAGE" ? `-${d.value}%` : `-${RSD(d.value)}`;
}
const toLocalInput = (iso) => {
  if (!iso) return "";
  const dt = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}T${p(dt.getHours())}:${p(dt.getMinutes())}`;
};

function DiscountSection({ product, onChanged }) {
  const d = product.discount;
  const status = product.discount_status || "NONE";
  const [open, setOpen] = useState(false);
  const [type, setType] = useState(d?.type || "PERCENTAGE");
  const [value, setValue] = useState(d?.value ?? 20);
  const [startMode, setStartMode] = useState(d?.starts_at ? "scheduled" : "now");
  const [startsAt, setStartsAt] = useState(toLocalInput(d?.starts_at));
  const [endMode, setEndMode] = useState(d?.ends_at ? "date" : "manual");
  const [endsAt, setEndsAt] = useState(toLocalInput(d?.ends_at));
  const [busy, setBusy] = useState(false);
  const base = Number(product.price) || 0;
  const preview = type === "PERCENTAGE" ? base * (1 - Number(value || 0) / 100) : base - Number(value || 0);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/products/${product.id}/discount`, {
        type, value: Number(value),
        starts_at: startMode === "scheduled" && startsAt ? startsAt : null,
        ends_at: endMode === "date" && endsAt ? endsAt : null,
      });
      toast.success("Popust sačuvan"); setOpen(false); onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Greška pri čuvanju popusta");
    } finally { setBusy(false); }
  };
  const off = async () => {
    setBusy(true);
    try { await api.delete(`/admin/products/${product.id}/discount`); toast.success("Popust deaktiviran"); setOpen(false); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Greška"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border-t pt-4 mt-4" data-testid="discount-section">
      <div className="flex items-center justify-between">
        <h4 className="font-display font-black text-sm uppercase tracking-wide">Popust</h4>
        <span data-testid="discount-status" className={`text-[10px] font-black px-2 py-1 rounded-full ${DISC_CLS[status]}`}>{DISC_LABEL[status]}</span>
      </div>
      {["ACTIVE", "SCHEDULED"].includes(status) && !open && (
        <div className="mt-2 bg-brand-green/5 border border-brand-green/30 rounded-xl p-3">
          <p className="font-display font-black text-brand-green" data-testid="discount-active-value">
            {status === "ACTIVE" ? "POPUST AKTIVAN" : "POPUST ZAKAZAN"} · {discountLabel(d)}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            {RSD(base)} → <b className="text-brand-green">{RSD(d.final_price)}</b>
            {d.ends_at ? ` · do ${new Date(d.ends_at).toLocaleString("sr-RS", { dateStyle: "short", timeStyle: "short" })}` : " · do ručnog isključenja"}
          </p>
          <div className="flex gap-2 mt-2">
            <button data-testid="discount-edit" onClick={() => setOpen(true)} className="bg-slate-800 text-white rounded-lg px-3 py-1.5 text-sm font-bold">Izmeni</button>
            <button data-testid="discount-deactivate" disabled={busy} onClick={off} className="bg-brand-red text-white rounded-lg px-3 py-1.5 text-sm font-bold">Deaktiviraj</button>
          </div>
        </div>
      )}
      {!["ACTIVE", "SCHEDULED"].includes(status) && !open && (
        <button data-testid="discount-activate" onClick={() => setOpen(true)} className="mt-2 bg-brand-green text-white rounded-lg px-4 py-2 font-bold text-sm">Aktiviraj popust</button>
      )}
      {open && (
        <div className="mt-3 space-y-3 bg-slate-50 rounded-xl p-3">
          <div className="flex gap-2">
            {[["PERCENTAGE", "Procenat"], ["FIXED", "Fiksni iznos"]].map(([v, l]) => (
              <button key={v} data-testid={`discount-type-${v}`} onClick={() => setType(v)}
                className={`flex-1 rounded-lg py-2 text-sm font-bold ${type === v ? "bg-brand-red text-white" : "bg-white border-2 border-slate-200 text-slate-600"}`}>{l}</button>
            ))}
          </div>
          <Field label={type === "PERCENTAGE" ? "Vrednost popusta (%)" : "Vrednost popusta (RSD)"}>
            <input data-testid="discount-value" type="number" className={inputCls} value={value} onChange={(e) => setValue(e.target.value)} />
          </Field>
          {type === "PERCENTAGE" && (
            <div className="flex flex-wrap gap-2">
              {PCT_PRESETS.map((p) => (
                <button key={p} data-testid={`discount-preset-${p}`} onClick={() => setValue(p)}
                  className={`px-3 py-1 rounded-full text-xs font-bold ${Number(value) === p ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{p}%</button>
              ))}
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Početak">
              <select data-testid="discount-start-mode" className={inputCls} value={startMode} onChange={(e) => setStartMode(e.target.value)}>
                <option value="now">Odmah</option>
                <option value="scheduled">Zakazano</option>
              </select>
              {startMode === "scheduled" && <input data-testid="discount-starts-at" type="datetime-local" className={inputCls + " mt-2"} value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />}
            </Field>
            <Field label="Kraj">
              <select data-testid="discount-end-mode" className={inputCls} value={endMode} onChange={(e) => setEndMode(e.target.value)}>
                <option value="date">Datum i vreme</option>
                <option value="manual">Do ručnog isključenja</option>
              </select>
              {endMode === "date" && <input data-testid="discount-ends-at" type="datetime-local" className={inputCls + " mt-2"} value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />}
            </Field>
          </div>
          <p className="text-xs text-slate-500">Bazna cena ostaje <b>{RSD(base)}</b>. Cena za kupca: <b className="text-brand-green" data-testid="discount-preview">{RSD(Math.max(0, preview))}</b></p>
          <div className="flex gap-2">
            <button data-testid="discount-save" disabled={busy} onClick={save} className="bg-brand-green text-white rounded-lg px-4 py-2 font-bold text-sm">Sačuvaj popust</button>
            <button onClick={() => setOpen(false)} className="bg-slate-200 rounded-lg px-4 py-2 font-bold text-sm">Otkaži</button>
            {d && d.active && <button data-testid="discount-deactivate-inline" disabled={busy} onClick={off} className="bg-brand-red text-white rounded-lg px-4 py-2 font-bold text-sm ml-auto">Deaktiviraj</button>}
          </div>
        </div>
      )}
    </div>
  );
}

/* -------- Product editor -------- */
function ProductEditor({ product, restaurantId, categories, onClose, onSaved, onDiscountChanged }) {
  const [f, setF] = useState(() => product || { name: "", description: "", price: 0, image: "", category_id: categories[0]?.id, available: true });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name || !f.category_id) { toast.error("Naziv i kategorija su obavezni"); return; }
    const payload = { ...f, price: Number(f.price), restaurant_id: restaurantId };
    delete payload.discount;          // discount is managed by its own server-authoritative endpoint
    delete payload.discount_status;
    try {
      if (product?.id) await api.put(`/admin/products/${product.id}`, payload);
      else await api.post(`/admin/products`, payload);
      toast.success("Sačuvano"); onSaved();
    } catch { toast.error("Greška pri čuvanju"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4"><h3 className="font-display font-black text-xl">{product?.id ? "Izmeni proizvod" : "Novi proizvod"}</h3><button onClick={onClose} className="bg-slate-100 rounded-full p-2"><X size={16} /></button></div>
        <Field label="Naziv"><input data-testid="prod-name" className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} /></Field>
        <Field label="Opis"><textarea className={inputCls} value={f.description} onChange={(e) => set("description", e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Cena (RSD)"><input data-testid="prod-price" type="number" className={inputCls} value={f.price} onChange={(e) => set("price", e.target.value)} /></Field>
          <Field label="Kategorija"><select data-testid="prod-cat" className={inputCls} value={f.category_id} onChange={(e) => set("category_id", e.target.value)}>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
        </div>
        <ImageUpload label="Slika proizvoda" value={f.image} onChange={(v) => set("image", v)} hint="Preporučeni format: landscape 4:3 (npr. 1200 × 900 px ili veće)" />
        {product?.id
          ? <DiscountSection product={product} onChanged={onDiscountChanged} />
          : <p className="text-xs text-slate-400 border-t pt-3 mt-3">Popust se može dodati nakon što sačuvate proizvod.</p>}
        <p className="text-xs text-slate-400 border-t pt-3 mt-3">Opcije (sos, dodaci, veličine…) se dodaju i raspoređuju u sekciji <b>Opcije</b>, pa se povezuju sa proizvodom.</p>
        <button data-testid="save-product" onClick={save} className="w-full bg-brand-red text-white rounded-lg py-3 font-bold mt-4">Sačuvaj</button>
      </div>
    </div>
  );
}

export function MenuManager({ restaurants }) {
  const [rid, setRid] = useState(restaurants[0]?.id || "");
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [newCat, setNewCat] = useState("");
  const [scheduling, setScheduling] = useState(null);

  // Saving a schedule never touches sort: only the two availability fields are sent.
  const saveSchedule = async (payload) => {
    try {
      await api.put(`/admin/categories/${scheduling.id}`, payload);
      await load();
      return {};
    } catch (e) {
      const d = e?.response?.data?.detail;
      return { error: (typeof d === "string" ? d : d?.message) || "Čuvanje nije uspelo. Pokušajte ponovo." };
    }
  };
  const [filter, setFilter] = useState("all");
  const [savingOrder, setSavingOrder] = useState(false);
  const [denied, setDenied] = useState(false);
  const load = async () => {
    if (!rid) return;
    try {
      const { data } = await api.get(`/admin/menu?restaurant_id=${rid}`);
      setCategories(data.categories); setProducts(data.products);
      setEditing((e) => (e && e.id ? data.products.find((p) => p.id === e.id) || e : e));
    } catch (e) { if (e?.response?.status === 403) setDenied(true); }
  };
  useEffect(() => { load(); }, [rid]);
  useEffect(() => { if (!rid && restaurants[0]) setRid(restaurants[0].id); }, [restaurants]);
  const addCat = async () => { if (!newCat.trim()) return; await api.post("/admin/categories", { name: newCat, restaurant_id: rid }); setNewCat(""); load(); };
  const delCat = async (id) => { if (!window.confirm("Obrisati kategoriju i proizvode?")) return; await api.delete(`/admin/categories/${id}`); load(); };
  const toggleAvail = async (p) => { await api.patch(`/admin/products/${p.id}/availability`, { available: !p.available }); load(); };
  const delProd = async (id) => { if (!window.confirm("Obrisati proizvod?")) return; await api.delete(`/admin/products/${id}`); load(); };

  // Ordering: optimistic UI, ONE request on drop/click, canonical rollback on failure.
  const persistOrder = async (call, rollback) => {
    setSavingOrder(true);
    try { await call(); toast.success("Redosled sačuvan"); }
    catch { rollback(); toast.error("Redosled nije sačuvan. Pokušajte ponovo."); }
    finally { setSavingOrder(false); }
  };
  const reorderCats = (ids) => {
    const prev = categories;
    const next = ids.map((id) => prev.find((c) => c.id === id));
    setCategories(next);
    persistOrder(() => api.patch("/admin/categories/reorder", { restaurant_id: rid, category_ids: ids }),
      () => setCategories(prev));
  };
  const reorderProds = (cid, ids) => {
    const prev = products;
    const byId = Object.fromEntries(prev.map((p) => [p.id, p]));
    const others = prev.filter((p) => p.category_id !== cid);
    setProducts([...others, ...ids.map((id) => ({ ...byId[id] }))]);
    persistOrder(() => api.patch(`/admin/categories/${cid}/products/reorder`, { product_ids: ids }),
      () => setProducts(prev));
  };
  const moveInList = (ids, from, to, apply) => {
    if (to < 0 || to >= ids.length) return;
    apply(arrayMove(ids, from, to));
  };

  // Quick rename: canonical PUT with only { name }; the backend response name wins. Only one
  // inline editor is open at a time and its row's reorder controls are disabled meanwhile.
  const [renaming, setRenaming] = useState(null);
  const renameProduct = async (p, name) => {
    try {
      const { data } = await api.put(`/admin/products/${p.id}`, { name });
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, name: data.name } : x)));
      toast.success("Naziv sačuvan");
      return true;
    } catch { toast.error("Naziv nije sačuvan. Pokušajte ponovo."); return false; }
  };
  const renameCategory = async (c, name) => {
    try {
      const { data } = await api.put(`/admin/categories/${c.id}`, { name });
      setCategories((prev) => prev.map((x) => (x.id === c.id ? { ...x, name: data.name } : x)));
      toast.success("Naziv sačuvan");
      return true;
    } catch { toast.error("Naziv nije sačuvan. Pokušajte ponovo."); return false; }
  };
  const renameEditing = (id) => (v) => setRenaming(v ? id : (r) => (r === id ? null : r));

  const matches = (p) => {
    const s = p.discount_status || "NONE";
    if (filter === "active") return s === "ACTIVE";
    if (filter === "scheduled") return s === "SCHEDULED";
    if (filter === "none") return !["ACTIVE", "SCHEDULED"].includes(s);
    return true;
  };
  const visible = products.filter(matches);
  // A reorder payload must carry the complete sibling set, so product dragging is only offered
  // on the unfiltered list.
  const prodDragDisabled = filter !== "all";
  const catIds = categories.map((c) => c.id);
  if (denied) {
    return (
      <div data-testid="menu-no-access" className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
        <p className="font-display font-black text-lg">Nemate pristup meniju</p>
        <p className="text-sm text-slate-500 mt-1">Za izmenu menija potrebna je OWNER ili MANAGER rola.</p>
      </div>
    );
  }
  return (
    <div>
      <div className="flex flex-wrap gap-2 items-center mb-5">
        <select data-testid="menu-rest-select" className={inputCls + " max-w-xs"} value={rid} onChange={(e) => setRid(e.target.value)}>{restaurants.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
        <input data-testid="new-cat-input" value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Nova kategorija" className={inputCls + " max-w-xs"} />
        <button data-testid="add-cat" onClick={addCat} className="bg-slate-800 text-white rounded-lg px-4 py-2 font-semibold flex items-center gap-1"><Plus size={16} />Kategorija</button>
        <button data-testid="new-product" onClick={() => setEditing({})} className="bg-brand-red text-white rounded-lg px-4 py-2 font-semibold flex items-center gap-1 ml-auto"><Plus size={16} />Novi proizvod</button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        {[["all", "Svi"], ["active", "Aktivni popusti"], ["scheduled", "Zakazani popusti"], ["none", "Bez popusta"]].map(([k, l]) => (
          <button key={k} data-testid={`menu-filter-${k}`} onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold ${filter === k ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
        {savingOrder && <span data-testid="order-saving" className="text-xs font-semibold text-slate-400">Čuvanje redosleda…</span>}
      </div>
      <SortableList ids={catIds} onReorder={reorderCats}>
        {categories.map((c, ci) => {
          const catProds = visible.filter((p) => p.category_id === c.id);
          const prodIds = catProds.map((p) => p.id);
          return (
            <SortableRow key={c.id} id={c.id} index={ci} total={categories.length}
              onMove={(f, t) => moveInList(catIds, f, t, reorderCats)}
              handleLabel="Promeni redosled kategorije" testid={`cat-row-${c.id}`}
              disabled={renaming === c.id}
              className="mb-6 flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <h3 className="font-display font-black text-lg min-w-0 flex-1">
                    <InlineRename value={c.name} testid={`cat-${c.id}`} onEditingChange={renameEditing(c.id)}
                      isActive={renaming === null || renaming === c.id}
                      onSave={(name) => renameCategory(c, name)} />
                  </h3>
                  <ScheduleBadge status={c.schedule_status} testid={`cat-schedule-badge-${c.id}`} />
                  <button data-testid={`category-schedule-${c.id}`} onClick={() => setScheduling(c)}
                    title="Vremenska dostupnost"
                    className={`shrink-0 ${c.availability_enabled ? "text-brand-red" : "text-slate-400 hover:text-brand-red"}`}>
                    <Clock size={16} />
                  </button>
                  <button data-testid={`delete-category-${c.id}`} onClick={() => delCat(c.id)} className="text-slate-400 hover:text-brand-red shrink-0"><Trash2 size={16} /></button>
                </div>
                <div className="bg-white rounded-xl border overflow-hidden">
                  <SortableList ids={prodIds} onReorder={(ids) => reorderProds(c.id, ids)} disabled={prodDragDisabled}>
                    {catProds.map((p, pi) => (
                      <SortableRow key={p.id} id={p.id} index={pi} total={catProds.length} disabled={prodDragDisabled || renaming === p.id}
                        onMove={(f, t) => moveInList(prodIds, f, t, (ids) => reorderProds(c.id, ids))}
                        handleLabel="Promeni redosled artikla" testid={`prod-row-${p.id}`}
                        className="flex items-center gap-2 p-3 border-b last:border-0">
                        <div className="flex items-center gap-3 flex-1 min-w-0" data-testid={`admin-prod-${p.id}`}>
                          {p.image ? <img src={imgURL(p.image)} className="w-12 h-12 rounded-lg object-cover" alt="" /> : <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-brand-yellow to-brand-orange" />}
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold flex min-w-0">
                              <InlineRename value={p.name} testid={`prod-${p.id}`} onEditingChange={renameEditing(p.id)}
                                isActive={renaming === null || renaming === p.id}
                                onSave={(name) => renameProduct(p, name)} />
                            </div>
                            {["ACTIVE", "SCHEDULED"].includes(p.discount_status) ? (
                              <div className="text-sm flex flex-wrap items-center gap-2" data-testid={`prod-discount-${p.id}`}>
                                <span className="line-through text-slate-400 text-xs">{RSD(p.price)}</span>
                                <span className="text-brand-green font-bold">{RSD(p.discount.final_price)}</span>
                                <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${DISC_CLS[p.discount_status]}`}>
                                  POPUST {discountLabel(p.discount)} · {DISC_LABEL[p.discount_status]}
                                </span>
                              </div>
                            ) : (
                              <p className="text-sm text-slate-400 flex items-center gap-2">
                                {RSD(p.price)}
                                {p.discount_status === "EXPIRED" && <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-slate-200 text-slate-500">POPUST ISTEKAO</span>}
                              </p>
                            )}
                          </div>
                          <button data-testid={`avail-${p.id}`} onClick={() => toggleAvail(p)} className={`text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 ${p.available ? "bg-brand-green/10 text-brand-green" : "bg-slate-200 text-slate-500"}`}><Power size={12} />{p.available ? "Dostupno" : "Nedostupno"}</button>
                          <button data-testid={`edit-product-${p.id}`} onClick={() => setEditing(p)} className="text-slate-400 hover:text-slate-800"><Pencil size={16} /></button>
                          <button data-testid={`delete-product-${p.id}`} onClick={() => delProd(p.id)} className="text-slate-400 hover:text-brand-red"><Trash2 size={16} /></button>
                        </div>
                      </SortableRow>
                    ))}
                  </SortableList>
                  {catProds.length === 0 && <p className="p-4 text-sm text-slate-300">Nema proizvoda</p>}
                </div>
              </div>
            </SortableRow>
          );
        })}
      </SortableList>
      {editing !== null && <ProductEditor product={editing.id ? editing : null} restaurantId={rid} categories={categories} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} onDiscountChanged={load} />}
      {scheduling && <CategoryScheduleEditor category={scheduling} onClose={() => setScheduling(null)} onSave={saveSchedule} />}
    </div>
  );
}

function UsersManager({ restaurants }) {
  const [users, setUsers] = useState([]);
  const load = async () => { const { data } = await api.get("/admin/users"); setUsers(data); };
  useEffect(() => { load(); }, []);
  const setRole = async (u, role) => { await api.patch(`/admin/users/${u.user_id}/role`, { role }); toast.success("Sačuvano"); load(); };
  return (
    <div className="bg-white rounded-xl border overflow-hidden max-w-3xl">
      {users.map((u) => (
        <div key={u.user_id} className="flex items-center gap-3 p-3 border-b last:border-0">
          {u.picture ? <img src={u.picture} className="w-9 h-9 rounded-full" alt="" /> : <div className="w-9 h-9 rounded-full bg-slate-200" />}
          <div className="flex-1 min-w-0"><p className="font-semibold truncate">{u.name || "—"}</p><p className="text-xs text-slate-400">{u.email}</p></div>
          <select data-testid={`role-${u.email}`} value={u.role === "admin" ? "admin" : "customer"} onChange={(e) => setRole(u, e.target.value)} className="px-2 py-1.5 rounded-lg border-2 border-slate-200 text-sm"><option value="customer">Kupac</option><option value="admin">Admin</option></select>
        </div>
      ))}
    </div>
  );
}

function OrdersManager() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/admin/orders").then(({ data }) => setOrders(data)); }, []);
  const stats = { total: orders.length, revenue: orders.filter((o) => o.status === "completed").reduce((s, o) => s + o.total, 0), active: orders.filter((o) => !["completed", "cancelled", "rejected"].includes(o.status)).length };
  return (
    <div>
      <div className="grid grid-cols-3 gap-4 mb-5 max-w-2xl">
        {[["Ukupno", stats.total], ["Aktivne", stats.active], ["Prihod", RSD(stats.revenue)]].map(([l, v]) => (<div key={l} className="bg-white rounded-xl border p-4"><p className="text-xs text-slate-400 uppercase font-semibold">{l}</p><p className="font-display font-black text-2xl mt-1">{v}</p></div>))}
      </div>
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-400 border-b"><th className="p-3">#</th><th className="p-3">Restoran</th><th className="p-3">Kupac</th><th className="p-3">Vreme</th><th className="p-3">Ukupno</th><th className="p-3">Status</th></tr></thead>
          <tbody>{orders.map((o) => (<tr key={o.id} className="border-b last:border-0"><td className="p-3 font-bold">#{o.order_number}</td><td className="p-3">{o.restaurant_name}</td><td className="p-3">{o.customer_name}</td><td className="p-3">{o.pickup_time}</td><td className="p-3">{RSD(o.total)}</td><td className="p-3"><span className="text-xs font-bold uppercase text-brand-red">{o.status}</span></td></tr>))}</tbody>
        </table>
      </div>
    </div>
  );
}

function TransRow({ srLabel, children }) {
  return (
    <div className="grid grid-cols-2 gap-3 items-start py-2 border-b last:border-0">
      <div className="text-sm text-slate-700 pt-2"><span className="text-[10px] uppercase tracking-wide text-slate-400 block">SR</span>{srLabel || <span className="text-slate-300">—</span>}</div>
      <div>{children}</div>
    </div>
  );
}
function TransSection({ title, children }) {
  return (<div className="mb-6 bg-white rounded-xl border p-4"><h3 className="font-display font-black text-lg mb-2">{title}</h3>{children}</div>);
}
function CatTrans({ c, onSave }) {
  const [en, setEn] = useState(c.name_en || "");
  return (<TransRow srLabel={c.name}><div className="flex gap-2">
    <input data-testid={`trans-cat-en-${c.id}`} className={inputCls} value={en} onChange={(e) => setEn(e.target.value)} placeholder="English" />
    <button data-testid={`trans-cat-save-${c.id}`} onClick={() => onSave(c, en)} className="bg-brand-red text-white rounded-lg px-3 font-bold text-sm">Sačuvaj</button>
  </div></TransRow>);
}
function ProdTrans({ p, onSave }) {
  const [name, setName] = useState(p.name_en || "");
  const [desc, setDesc] = useState(p.description_en || "");
  return (
    <div className="py-3 border-b last:border-0">
      <TransRow srLabel={p.name}><input data-testid={`trans-prod-name-en-${p.id}`} className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="English name" /></TransRow>
      {(p.description || p.description_en) && (
        <TransRow srLabel={p.description}><textarea data-testid={`trans-prod-desc-en-${p.id}`} rows={2} className={inputCls + " resize-none"} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="English description" /></TransRow>
      )}
      <div className="text-right mt-2"><button data-testid={`trans-prod-save-${p.id}`} onClick={() => onSave(p, name, desc)} className="bg-brand-red text-white rounded-lg px-4 py-1.5 font-bold text-sm">Sačuvaj</button></div>
    </div>
  );
}
function GroupTrans({ g, onSave }) {
  const [name, setName] = useState(g.name_en || "");
  const [items, setItems] = useState(() => (g.items || []).map((i) => ({ id: i.id, name: i.name, name_en: i.name_en || "" })));
  const setItemEn = (id, v) => setItems((prev) => prev.map((i) => (i.id === id ? { ...i, name_en: v } : i)));
  return (
    <div className="py-3 border-b last:border-0">
      <TransRow srLabel={g.name}><input data-testid={`trans-group-name-en-${g.id}`} className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="English group name" /></TransRow>
      <div className="pl-3 mt-2 border-l-2 border-slate-100">
        {items.map((i) => (<TransRow key={i.id} srLabel={i.name}><input data-testid={`trans-item-en-${i.id}`} className={inputCls} value={i.name_en} onChange={(e) => setItemEn(i.id, e.target.value)} placeholder="English" /></TransRow>))}
      </div>
      <div className="text-right mt-2"><button data-testid={`trans-group-save-${g.id}`} onClick={() => onSave(g, name, items.map((i) => ({ id: i.id, name_en: i.name_en })))} className="bg-brand-red text-white rounded-lg px-4 py-1.5 font-bold text-sm">Sačuvaj</button></div>
    </div>
  );
}
function TranslationsManager({ restaurants }) {
  const [rid, setRid] = useState(restaurants[0]?.id || "");
  const [data, setData] = useState({ categories: [], products: [], option_groups: [] });
  const load = async () => { if (!rid) return; const { data } = await api.get(`/admin/translations?restaurant_id=${rid}`); setData(data); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rid]);
  const saveCat = async (c, name_en) => { await api.put(`/admin/translations/category/${c.id}`, { name_en }); toast.success("Prevod sačuvan"); load(); };
  const saveProd = async (p, name_en, description_en) => { await api.put(`/admin/translations/product/${p.id}`, { name_en, description_en }); toast.success("Prevod sačuvan"); load(); };
  const saveGroup = async (g, name_en, items) => { await api.put(`/admin/translations/option-group/${g.id}`, { name_en, items }); toast.success("Prevod sačuvan"); load(); };
  return (
    <div className="max-w-3xl" data-testid="translations-manager">
      <select data-testid="trans-rest-select" className={inputCls + " max-w-xs mb-4"} value={rid} onChange={(e) => setRid(e.target.value)}>{restaurants.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
      <p className="text-sm text-slate-500 mb-4">Ispravite engleski prevod (prikazuje se na /en). Ručne izmene se ne prepisuju automatskim prevodom dok se srpski tekst ne promeni.</p>
      <TransSection title="Kategorije">{data.categories.map((c) => <CatTrans key={c.id} c={c} onSave={saveCat} />)}</TransSection>
      <TransSection title="Proizvodi">{data.products.map((p) => <ProdTrans key={p.id} p={p} onSave={saveProd} />)}</TransSection>
      <TransSection title="Grupe opcija">{data.option_groups.map((g) => <GroupTrans key={g.id} g={g} onSave={saveGroup} />)}</TransSection>
    </div>
  );
}

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const nav = isAdmin ? NAV : NAV.filter((n) => ["restaurants", "menu", "options", "translations"].includes(n.key));
  const [tab, setTab] = useState(isAdmin ? "restaurants" : "menu");
  const [restaurants, setRestaurants] = useState([]);
  const loadRest = async () => { const { data } = await api.get("/my/restaurants"); setRestaurants(data); };
  useEffect(() => { loadRest(); }, []);
  return (
    <div className="min-h-screen bg-[#F3F4F6] flex">
      <aside className="w-56 bg-white border-r p-4 hidden md:flex flex-col">
        <div className="flex items-center gap-2 mb-6"><BagLogo size={34} /><span className="font-display font-black text-lg">Ponesi <span className="text-brand-red">{isAdmin ? "Admin" : "Restoran"}</span></span></div>
        <nav className="space-y-1 flex-1">{nav.map((n) => { const Icon = n.icon; return (<button key={n.key} data-testid={`nav-${n.key}`} onClick={() => setTab(n.key)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg font-semibold text-sm ${tab === n.key ? "bg-brand-red text-white" : "text-slate-600 hover:bg-slate-100"}`}><Icon size={17} />{n.key === "restaurants" && !isAdmin ? "Moj restoran" : n.label}</button>); })}</nav>
        <button onClick={async () => { await logout(); window.location.href = "/admin/login"; }} className="text-sm text-brand-red font-semibold mt-4">Odjava</button>
      </aside>
      <div className="flex-1 min-w-0">
        <div className="md:hidden flex gap-2 p-3 overflow-x-auto bg-white border-b">{nav.map((n) => <button key={n.key} onClick={() => setTab(n.key)} className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap ${tab === n.key ? "bg-brand-red text-white" : "bg-slate-100"}`}>{n.key === "restaurants" && !isAdmin ? "Moj restoran" : n.label}</button>)}</div>
        <main className="p-6">
          <h1 className="font-display font-black text-2xl mb-5">{tab === "restaurants" && !isAdmin ? "Moj restoran" : nav.find((n) => n.key === tab)?.label}</h1>
          {tab === "restaurants" && <RestaurantsManager restaurants={restaurants} reload={loadRest} isAdmin={isAdmin} />}
          {tab === "menu" && <MenuManager restaurants={restaurants} />}
          {tab === "options" && <OptionsManager restaurants={restaurants} />}
          {tab === "translations" && <TranslationsManager restaurants={restaurants} />}
          {tab === "orders" && isAdmin && <OrdersManager />}
          {tab === "users" && isAdmin && <UsersManager restaurants={restaurants} />}
          {tab === "analytics" && isAdmin && <PlatformAnalytics />}
        </main>
      </div>
    </div>
  );
}
